import React, { useState, useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Calendar, User, ArrowRight, Search, Newspaper, Phone, Sparkles } from 'lucide-react';
import Layout from '../components/Layout';
import { useLanguage } from '../context/LanguageContext';
import { useCmsContent } from '../context/CmsImagesContext';

function formatDate(dateStr, lang = 'da') {
  if (!dateStr) return '';
  try {
    const d = new Date(dateStr);
    if (isNaN(d.getTime())) return dateStr;
    return new Intl.DateTimeFormat(lang === 'da' ? 'da-DK' : 'en-US', {
      day: 'numeric',
      month: 'long',
      year: 'numeric'
    }).format(d);
  } catch {
    return dateStr;
  }
}

export function isCtaEntry(item) {
  if (!item) return false;
  const slugStr = typeof item.slug === 'object' ? (item.slug?.da || Object.values(item.slug)[0] || '') : (item.slug || '');
  const titleStr = typeof item.titel === 'object' ? (item.titel?.da || Object.values(item.titel)[0] || '') : (item.titel || '');
  return (
    slugStr.toLowerCase() === 'cta' ||
    slugStr.toLowerCase() === 'bundsektion-cta' ||
    slugStr.toLowerCase() === 'nyhedsside-cta' ||
    titleStr.toLowerCase().includes('cta') ||
    titleStr.toLowerCase().includes('bundsektion')
  );
}

export default function News() {
  const { t, language } = useLanguage();
  const { getImage, companyInfo } = useCmsContent();
  const [articles, setArticles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');

  useEffect(() => {
    document.title = t({
      da: "Nyheder & Råd | FTR Service ApS",
      en: "News & Advice | FTR Service ApS"
    });
  }, [t]);

  useEffect(() => {
    let isMounted = true;

    async function fetchNews() {
      try {
        setLoading(true);
        // Try v2 list endpoint with sorting by date desc
        const res = await fetch('https://api.dev.auxo.dk/public/v2/content/nyheder?sort=dato&order=desc');
        if (!res.ok) throw new Error('Failed to fetch from v2');
        const data = await res.json();
        
        if (isMounted) {
          const items = Array.isArray(data.items) ? data.items : (Array.isArray(data) ? data : []);
          setArticles(items);
        }
      } catch (err) {
        console.warn('Could not fetch news from v2, trying standard endpoint:', err);
        try {
          const fallbackRes = await fetch('https://api.dev.auxo.dk/public/content/nyheder');
          if (fallbackRes.ok) {
            const fallbackData = await fallbackRes.json();
            if (isMounted && Array.isArray(fallbackData)) {
              setArticles(fallbackData);
            }
          }
        } catch (fallbackErr) {
          console.error('Error fetching fallback news:', fallbackErr);
        }
      } finally {
        if (isMounted) setLoading(false);
      }
    }

    fetchNews();

    return () => {
      isMounted = false;
    };
  }, []);

  const getLocalizedField = (field) => {
    if (!field) return '';
    if (typeof field === 'string') return field;
    if (typeof field === 'object') {
      return field[language] || field.da || Object.values(field)[0] || '';
    }
    return '';
  };

  const getSlug = (item) => {
    if (!item) return '';
    if (item.slug) {
      if (typeof item.slug === 'string') return item.slug;
      if (typeof item.slug === 'object') {
        const langSlug = item.slug[language];
        if (langSlug && !/^[0-9a-f]{8}-[0-9a-f]{4}/i.test(langSlug)) return langSlug;
        if (item.slug.da) return item.slug.da;
        return langSlug || Object.values(item.slug)[0] || item.id;
      }
    }
    return item.id;
  };

  const getImageSrc = (item) => {
    if (!item) return '/images/hero-cleaning.png';
    let url = null;
    if (Array.isArray(item.billede) && item.billede.length > 0) {
      url = item.billede[0];
    } else if (typeof item.billede === 'string' && item.billede.trim()) {
      url = item.billede.trim();
    }
    if (url && typeof url === 'string') {
      if (url.startsWith('https://assets.auxodev.dk/') && !url.includes('/cdn-cgi/image/')) {
        return url.replace('https://assets.auxodev.dk/', 'https://assets.auxodev.dk/cdn-cgi/image/width=800,quality=80,format=auto/');
      }
      return url;
    }
    return '/images/hero-cleaning.png';
  };

  const getExcerpt = (article) => {
    if (!article) return '';
    const directExcerpt = getLocalizedField(article.uddrag);
    if (directExcerpt) return directExcerpt;
    const body = getLocalizedField(article.beskrivelse) || getLocalizedField(article.indhold) || '';
    if (!body) return '';
    const cleanText = body.replace(/<[^>]*>/g, ' ').replace(/\s+/g, ' ').trim();
    if (cleanText.length <= 180) return cleanText;
    return cleanText.slice(0, 180) + '...';
  };

  const ctaEntry = useMemo(() => {
    return articles.find(isCtaEntry) || null;
  }, [articles]);

  const newsArticles = useMemo(() => {
    return articles.filter((item) => !isCtaEntry(item));
  }, [articles]);

  const filteredArticles = useMemo(() => {
    if (!searchQuery.trim()) return newsArticles;
    const query = searchQuery.toLowerCase().trim();
    return newsArticles.filter((item) => {
      const title = getLocalizedField(item.titel).toLowerCase();
      const excerpt = getExcerpt(item).toLowerCase();
      const author = (item.forfatter || '').toLowerCase();
      return title.includes(query) || excerpt.includes(query) || author.includes(query);
    });
  }, [newsArticles, searchQuery, language]);

  const ctaBadge = language === 'en' 
    ? (typeof ctaEntry?.cta_badge === 'object' && ctaEntry?.cta_badge?.en ? ctaEntry.cta_badge.en : t({ da: "Professionel rengøring", en: "Professional cleaning" }))
    : (getLocalizedField(ctaEntry?.cta_badge) || t({ da: "Professionel rengøring", en: "Professional cleaning" }));

  const ctaOverskrift = language === 'en'
    ? (typeof ctaEntry?.cta_overskrift === 'object' && ctaEntry?.cta_overskrift?.en ? ctaEntry.cta_overskrift.en : t({ da: "Skal vi hjælpe dig med rengøringen?", en: "Need help with cleaning?" }))
    : (getLocalizedField(ctaEntry?.cta_overskrift) || t({ da: "Skal vi hjælpe dig med rengøringen?", en: "Need help with cleaning?" }));

  const ctaTekst = language === 'en'
    ? (typeof ctaEntry?.cta_tekst === 'object' && ctaEntry?.cta_tekst?.en ? ctaEntry.cta_tekst.en : t({
        da: "Vi servicerer både private hjem og virksomheder i Billund, Varde og Vejle kommune. Få et skræddersyet tilbud der matcher netop dine behov.",
        en: "We service private homes and businesses in Billund, Varde and Vejle municipality. Get a tailored offer matching your exact needs."
      }))
    : (getLocalizedField(ctaEntry?.cta_tekst) || t({
        da: "Vi servicerer både private hjem og virksomheder i Billund, Varde og Vejle kommune. Få et skræddersyet tilbud der matcher netop dine behov.",
        en: "We service private homes and businesses in Billund, Varde and Vejle municipality. Get a tailored offer matching your exact needs."
      }));

  const ctaKnapTekst = language === 'en'
    ? (typeof ctaEntry?.cta_knap_tekst === 'object' && ctaEntry?.cta_knap_tekst?.en ? ctaEntry.cta_knap_tekst.en : t({ da: "Kontakt os i dag", en: "Contact us today" }))
    : (getLocalizedField(ctaEntry?.cta_knap_tekst) || t({ da: "Kontakt os i dag", en: "Contact us today" }));
  const ctaKnapLink = ctaEntry?.cta_knap_link || "/kontakt";

  return (
    <Layout>
      {/* Hero Header */}
      <section className="relative py-24 bg-gradient-to-b from-[#1b3157] to-[#12223c] text-white overflow-hidden">
        <div className="absolute inset-0 opacity-20 bg-[radial-gradient(#85d7f9_1px,transparent_1px)] [background-size:16px_16px]" />
        
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="max-w-3xl mx-auto space-y-6"
          >
            <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-white/10 backdrop-blur-md text-[#85d7f9] text-sm font-medium border border-white/10">
              <Newspaper className="w-4 h-4" />
              <span>{t({ da: "Aktuelt & Viden", en: "News & Insights" })}</span>
            </div>

            <h1 className="text-4xl md:text-5xl lg:text-6xl font-extrabold tracking-tight">
              {t({ da: "Nyheder & Råd", en: "News & Advice" })}
            </h1>

            <p className="text-lg md:text-xl text-gray-300 leading-relaxed">
              {t({
                da: "Få gode råd om rengøring, hold dig opdateret om servicefradraget og følg med i hverdagen hos FTR Service ApS.",
                en: "Get good cleaning advice, stay updated on the service deduction, and follow everyday life at FTR Service ApS."
              })}
            </p>

            {/* Search Bar */}
            <div className="pt-4 max-w-md mx-auto">
              <div className="relative">
                <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                <input
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder={t({ da: "Søg i nyheder og artikler...", en: "Search news and articles..." })}
                  className="w-full pl-12 pr-4 py-3.5 bg-white/10 backdrop-blur-md border border-white/20 rounded-full text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-[#85d7f9] focus:border-transparent transition-all shadow-inner"
                />
                {searchQuery && (
                  <button
                    onClick={() => setSearchQuery('')}
                    className="absolute right-4 top-1/2 -translate-y-1/2 text-xs text-gray-400 hover:text-white"
                  >
                    {t({ da: "Ryd", en: "Clear" })}
                  </button>
                )}
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Articles Grid Section */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {loading ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {[1, 2, 3].map((n) => (
                <div key={n} className="bg-white rounded-3xl overflow-hidden shadow-sm border border-gray-100 animate-pulse">
                  <div className="h-56 bg-gray-200" />
                  <div className="p-6 space-y-4">
                    <div className="h-4 bg-gray-200 rounded w-1/3" />
                    <div className="h-6 bg-gray-200 rounded w-4/5" />
                    <div className="h-4 bg-gray-200 rounded w-full" />
                    <div className="h-4 bg-gray-200 rounded w-2/3" />
                  </div>
                </div>
              ))}
            </div>
          ) : filteredArticles.length === 0 ? (
            <div className="text-center py-16 bg-white rounded-3xl border border-gray-100 shadow-sm max-w-xl mx-auto p-8">
              <Newspaper className="w-12 h-12 text-gray-300 mx-auto mb-4" />
              <h3 className="text-xl font-bold text-gray-900 mb-2">
                {searchQuery ? t({ da: "Ingen nyheder matchede din søgning", en: "No news matched your search" }) : t({ da: "Der er ingen nyheder endnu", en: "No news articles yet" })}
              </h3>
              <p className="text-gray-500 text-sm mb-6">
                {searchQuery 
                  ? t({ da: "Prøv at søge på et andet ord eller ryd søgefeltet.", en: "Try searching for another word or clear the search field." })
                  : t({ da: "Kig forbi snart igen for nye opdateringer og gode rengøringsråd.", en: "Check back soon for new updates and great cleaning advice." })}
              </p>
              {searchQuery && (
                <button
                  onClick={() => setSearchQuery('')}
                  className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-brand-primary text-white font-medium text-sm hover:bg-[#152744] transition-all"
                >
                  {t({ da: "Vis alle nyheder", en: "Show all news" })}
                </button>
              )}
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {filteredArticles.map((article, idx) => {
                const title = getLocalizedField(article.titel);
                const excerpt = getExcerpt(article);
                const slug = getSlug(article);
                const imageSrc = getImageSrc(article);
                const formattedDate = formatDate(article.dato, language);
                const author = article.forfatter || 'FTR Service';

                return (
                  <motion.article
                    key={article.id || idx}
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.5, delay: idx * 0.1 }}
                    className="group bg-white rounded-3xl overflow-hidden shadow-sm hover:shadow-xl transition-all duration-300 border border-gray-100 flex flex-col h-full"
                  >
                    {/* Image Container */}
                    <Link to={`/nyheder/${slug}`} className="relative h-56 overflow-hidden block bg-gray-100">
                      <img
                        src={imageSrc}
                        alt={title}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                        loading="lazy"
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-black/40 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                      {formattedDate && (
                        <div className="absolute top-4 left-4 bg-white/95 backdrop-blur-md px-3 py-1 rounded-full text-xs font-semibold text-brand-primary shadow-sm flex items-center gap-1.5">
                          <Calendar className="w-3.5 h-3.5 text-[#85d7f9]" />
                          <span>{formattedDate}</span>
                        </div>
                      )}
                    </Link>

                    {/* Content Container */}
                    <div className="p-6 flex-1 flex flex-col justify-between">
                      <div className="space-y-3">
                        <div className="flex items-center gap-2 text-xs text-gray-500 font-medium">
                          <User className="w-3.5 h-3.5 text-gray-400" />
                          <span>{author}</span>
                        </div>

                        <h2 className="text-xl font-bold text-gray-900 group-hover:text-[#1877F2] transition-colors line-clamp-2">
                          <Link to={`/nyheder/${slug}`}>{title}</Link>
                        </h2>

                        <p className="text-gray-600 text-sm leading-relaxed line-clamp-3">
                          {excerpt}
                        </p>
                      </div>

                      <div className="pt-6 mt-4 border-t border-gray-50 flex items-center justify-between">
                        <Link
                          to={`/nyheder/${slug}`}
                          className="inline-flex items-center gap-2 text-sm font-semibold text-brand-primary group-hover:text-[#1877F2] transition-colors"
                        >
                          <span>{t({ da: "Læs hele artiklen", en: "Read full article" })}</span>
                          <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                        </Link>
                      </div>
                    </div>
                  </motion.article>
                );
              })}
            </div>
          )}
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-white border-t border-gray-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="relative rounded-3xl bg-gradient-to-r from-[#1b3157] to-[#25467d] p-8 md:p-14 text-white overflow-hidden shadow-xl">
            <div className="absolute right-0 bottom-0 translate-x-12 translate-y-12 w-80 h-80 bg-[#85d7f9]/10 rounded-full blur-3xl pointer-events-none" />
            
            <div className="relative z-10 max-w-2xl space-y-4">
              <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-white/10 text-xs font-semibold text-[#85d7f9] backdrop-blur-sm">
                <Sparkles className="w-3.5 h-3.5" />
                <span>{ctaBadge}</span>
              </div>
              <h2 className="text-3xl md:text-4xl font-extrabold tracking-tight">
                {ctaOverskrift}
              </h2>
              <p className="text-gray-300 text-base leading-relaxed">
                {ctaTekst}
              </p>
              <div className="pt-4 flex flex-wrap items-center gap-4">
                <Link
                  to={ctaKnapLink}
                  className="px-6 py-3.5 rounded-full bg-[#85d7f9] text-[#1b3157] font-bold text-sm hover:bg-white transition-all shadow-lg hover:shadow-xl"
                >
                  {ctaKnapTekst}
                </Link>
                <a
                  href={`tel:${companyInfo?.telefon?.replace(/\s+/g, '') || '+4591847666'}`}
                  className="inline-flex items-center gap-2 px-6 py-3.5 rounded-full bg-white/10 border border-white/20 text-white font-semibold text-sm hover:bg-white/20 transition-all backdrop-blur-sm"
                >
                  <Phone className="w-4 h-4 text-[#85d7f9]" />
                  <span>{companyInfo?.telefon || '+45 91 84 76 66'}</span>
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>
    </Layout>
  );
}
